import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter, useHistory, useLocation } from "react-router-dom";
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import { motion } from 'framer-motion';
import { linkVariants, containerVariants } from '../utils/Variants';
import { pizzaData } from '../utils/pizzaData';
import { bannerData } from '../utils/bannerData';
import SelectPizza from '../Components/SelectPizza';
import { addProduct } from '../store/Cart/cartActions';
import Price from '../Components/Price';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Button } from '@material-ui/core';
import Carousel from 'react-material-ui-carousel';
import Footer from '../Components/Footer';
import Pagination from '@material-ui/lab/Pagination';
import usePagination from '../utils/pagination';
import { WhatsAppLink } from "../utils/whatsapp";
import useMediaQuery from '@material-ui/core/useMediaQuery';
import LazyImage from '../Components/LazyImage';

const Home = ({ addProduct, match, currency }) => {
    const history = useHistory();
    const location = useLocation();
    const { params } = match;
    const isMobile = useMediaQuery('(max-width:600px)');

    const [pizza, setPizza] = useState({ src: "", id: 1, base: "", toppings: [], name: "", price: 0, quantity: 1 });
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [filteredData, setFilteredData] = useState(pizzaData);
    const [activeIndex, setActiveIndex] = useState(0);
    const [showScroll, setShowScroll] = useState(false);

    const PER_PAGE = 12;
    const pagination = usePagination(filteredData, PER_PAGE);

    // ====== FILTERING ======
    const filter = (value) => {
        setFilteredData(
            !value ? pizzaData : pizzaData.filter(item => item.name.toLowerCase().includes(value.toLowerCase()))
        );
    };

    const filterDataProduct = () => {
        const find = params.p1?.match(/p1=([^&\/]+)/)?.[1].split(',') || [];
        return pizzaData.filter(item => find.includes(String(item.id)));
    }

    // ===== INITIAL LOAD =====
    useEffect(() => {
        setLoading(false);
        setFilteredData(params.p1 ? filterDataProduct() : pizzaData);

        const p2 = parseInt(new URLSearchParams(location.search).get("p2"), 10);
        if (!isNaN(p2) && p2 >= 0 && p2 < bannerData.length) setActiveIndex(p2);
    }, []);

    // Update pagination when filteredData changes
    useEffect(() => {
        pagination.setData(filteredData);
        pagination.jump(1);
    }, [filteredData]);

    // Scroll up button visibility
    useEffect(() => {
        const handleScroll = () => setShowScroll(window.scrollY > 200);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // ===== MODAL & CART HANDLERS =====
    // check
    const handleClickOpen = (name, price, id, src) => {
        if (!price) return;
        setPizza({ src, id, name, price, base: "", toppings: [], quantity: 1 });
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleAddToCart = () => {
        addProduct(pizza);
        setOpen(false);
    };

    const handleCheckout = () => {
        addProduct(pizza);
        setOpen(false);
        history.push('/checkout');
    };

    const addBase = (base) => setPizza(prev => ({ ...prev, base }));
    const setQuantity = (e) => setPizza(prev => ({ ...prev, quantity: Math.max(1, parseInt(e.target.value)) }));
    const addTopping = (topping) => setPizza(prev => ({
        ...prev,
        toppings: prev.toppings.includes(topping)
            ? prev.toppings.filter(item => item !== topping)
            : [...prev.toppings, topping]
    }));

    const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    const handleCarouselChange = (index) => {
        setActiveIndex(index);
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("p2", index);
        history.replace({ pathname: location.pathname, search: searchParams.toString() });
    };

    return loading ? (
        <div style={{ textAlign: 'center' }}>
            <img src="/loadingImg.gif" alt="loading" />
        </div>
    ) : (
        <div>
            <motion.div variants={containerVariants} initial="hidden" animate="visible" exit="exit">
                <Container>

                    {/* Carousel with LazyImage */}
                    <Carousel
                        index={activeIndex}
                        autoPlay={false}
                        navButtonsAlwaysVisible
                        navButtonsProps={{ style: { color: 'red' } }}
                        onChange={handleCarouselChange}
                    >
                        {bannerData.map((banner, idx) => (
                            <div className='Showcase_div' key={idx}>
                                <LazyImage 
                                    src={banner.src} 
                                    alt={`banner-${idx}`} 
                                    className='img-caraousel' 
                                    placeholder="/loadingImg.gif"
                                />
                                {banner.href && (
                                    <div className='button_corousel'>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            href={banner.href + (banner.target ? '' : '?p2=' + activeIndex)}
                                            target={banner.target || '_self'}
                                        >
                                            Selengkapnya
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </Carousel>

                    {/* Search */}
                    <Autocomplete
                        freeSolo
                        clearOnEscape
                        options={pizzaData.map(p => p.name)}
                        onInputChange={(e, value) => filter(value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Silahkan Cari..."
                                margin="normal"
                                variant="outlined"
                                type="search"
                                fullWidth
                                InputProps={{
                                    ...params.InputProps,
                                    style: {
                                        fontSize: isMobile ? "1.1rem" : "1rem",
                                        padding: isMobile ? "14px" : "8px",
                                    },
                                }}
                            />
                        )}
                    />

                    {/* Modal */}
                    <SelectPizza
                        pizza={pizza}
                        open={open}
                        handleClose={handleClose}
                        handleAddToCart={handleAddToCart}
                        handleCheckout={handleCheckout}
                        addBase={addBase}
                        addTopping={addTopping}
                        setQuantity={setQuantity}
                    />

                    {/* Pizza Grid */}
                    <Grid container spacing={2}>
                        {pagination.currentData().length ? pagination.currentData().map((pizza, i) => (
                            <Grid key={i} item xs={12} sm={i < 2 ? 6 : 4}>
                                <div onClick={() => handleClickOpen(pizza.name, pizza.price, pizza.id, pizza.src)}>
                                    <div className="pizzaContainer">
                                        <motion.div className="pizzaImgCon">
                                            <LazyImage src={`/pizzapics/${pizza.src}`} alt={pizza.name} className="pizzaImg" />
                                        </motion.div>
                                        <div className="pizzaInfo">
                                            <motion.h2 className="pizzaName" variants={linkVariants} whileHover="hover">
                                                {pizza.name}
                                            </motion.h2>
                                            <span className="pizzaPrice">
                                                {pizza.category === 'book'
                                                    ? <a href={pizza.link} target="_blank" rel="noopener noreferrer">Buka</a>
                                                    : <Price>{pizza.price}</Price>
                                                }
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Grid>
                        )) : <p>Tidak ditemukan</p>}
                    </Grid>

                    {/* Pagination */}
                    <div className="pagination-container">
                        <Pagination
                            count={pagination.maxPage}
                            page={pagination.currentPage}
                            onChange={(e, page) => { pagination.jump(page); scrollToTop(); }}
                            size="medium"
                            variant="outlined"
                            shape="rounded"
                            siblingCount={1}
                            boundaryCount={1}
                        />
                    </div>

                    {/* Scroll Up */}
                    {showScroll && (
                        <div className="scrollup_btn" onClick={scrollToTop}>
                            <i className="fa fa-arrow-up"></i>
                        </div>
                    )}

                    {/* WhatsApp */}
                    <WhatsAppLink phone="6281354950219" text="Halo, saya mau order..." />

                </Container>
            </motion.div>
            <Footer />
        </div>
    );
}

const mapStateToProps = state => ({ currency: state.total.currency });

export default connect(mapStateToProps, { addProduct })(withRouter(Home));
